import React from 'react';
import {
  KeyRound,
  ShieldCheck,
  ShieldAlert,
  UserCog,
  PauseCircle,
  Play,
  Trash2,
} from 'lucide-react';
import Pagination from '../common/Pagination';
import ActionMenu from '../common/ActionMenu';
import { useApp } from '../../contexts/AppContext';

const ROLE_LABELS = {
  admin: 'Administrateur',
  comptable: 'Comptable',
  user: 'Utilisateur',
};

const ROLE_ICONS = {
  admin: ShieldCheck,
  comptable: UserCog,
  user: ShieldAlert,
};

const UsersList = ({
  users,
  showUserForm,
  setShowUserForm,
  handleUserSubmit,
  resetUserPassword,
  changeUserRole,
  toggleUserActive,
  deleteUser,
  currentPageUsers,
  setCurrentPageUsers,
}) => {
  const { currentUser, itemsPerPage, setItemsPerPage, getPaginatedItems } = useApp();
  const filteredUsers = users.filter((u) => u.username !== 'youdemus');

  const buildMenuItems = (user) => {
    const items = [
      {
        label: 'Réinitialiser mot de passe',
        icon: KeyRound,
        onClick: () => resetUserPassword(user.id, user.username),
        testId: `menu-reset-pwd-${user.id}`,
      },
      {
        label: user.role === 'admin' ? 'Définir comme Comptable' : 'Définir comme Administrateur',
        icon: user.role === 'admin' ? UserCog : ShieldCheck,
        onClick: () =>
          changeUserRole(user.id, user.role === 'admin' ? 'comptable' : 'admin'),
        testId: `menu-set-role-1-${user.id}`,
      },
      {
        label: user.role !== 'user' ? 'Définir comme Utilisateur' : 'Garder Utilisateur',
        icon: ShieldAlert,
        onClick: () => changeUserRole(user.id, 'user'),
        disabled: user.role === 'user',
        testId: `menu-set-role-2-${user.id}`,
      },
      { type: 'separator' },
      {
        label: user.active ? 'Désactiver le compte' : 'Activer le compte',
        icon: user.active ? PauseCircle : Play,
        onClick: () => toggleUserActive(user.id, user.active),
        variant: user.active ? 'warning' : 'success',
        testId: `menu-toggle-active-${user.id}`,
      },
      { type: 'separator' },
      {
        label: 'Supprimer définitivement',
        icon: Trash2,
        onClick: () => deleteUser(user.id),
        variant: 'danger',
        testId: `menu-delete-${user.id}`,
      },
    ];
    return items;
  };

  return (
    <div className="users">
      <div className="section-header">
        <h2>Utilisateurs</h2>
        <div className="header-buttons">
          <button onClick={() => setShowUserForm(true)} className="btn btn-primary" data-testid="btn-new-user">
            Nouvel Utilisateur
          </button>
        </div>
      </div>

      {showUserForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Nouvel utilisateur</h3>
            <form onSubmit={handleUserSubmit}>
              <div className="form-group">
                <label>Nom d'utilisateur *</label>
                <input type="text" name="username" required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" required />
              </div>
              <div className="form-group">
                <label>Mot de passe *</label>
                <input type="password" name="password" required />
              </div>
              <div className="form-group">
                <label>Rôle *</label>
                <select name="role" required>
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                  <option value="comptable">Comptable</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Créer</button>
                <button type="button" onClick={() => setShowUserForm(false)} className="btn btn-secondary">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Statut</th>
              <th>Date de création</th>
              <th style={{ width: 80, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedItems(filteredUsers, currentPageUsers, itemsPerPage).map((user) => {
              const RoleIcon = ROLE_ICONS[user.role] || ShieldAlert;
              const isCurrent = currentUser?.username === user.username;
              return (
                <tr key={user.id}>
                  <td>
                    <strong>{user.username}</strong>
                    {isCurrent && <span className="current-user-badge" style={{ marginLeft: 8 }}>C'est vous</span>}
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge role-${user.role}`}>
                      <RoleIcon size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                      {ROLE_LABELS[user.role] || user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`user-status-badge ${user.active ? 'active' : 'inactive'}`}>
                      {user.active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td>
                    {user.created_at
                      ? new Date(user.created_at).toLocaleDateString('fr-FR')
                      : 'N/A'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {!isCurrent ? (
                      <ActionMenu items={buildMenuItems(user)} testId={`user-actions-${user.id}`} />
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPageUsers}
        totalItems={filteredUsers.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPageUsers}
        onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPageUsers(1); }}
      />
    </div>
  );
};

export default UsersList;
